import React from 'react';
import JsonLd from '../../components/JsonLd';
import SEOHead from '../../components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  Monitor, ListChecks, Database, Package, Layers, ArrowRight, CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const HardwareSupportPage = () => {
  const benefits = [
    {
      icon: ListChecks,
      title: 'Structured data capture for faster diagnosis',
      description:
        'The AI collects asset tag, device type, symptoms, and urgency upfront - creating a structured record that dramatically speeds up diagnosis and triage, whether resolved by AI or escalated.',
    },
    {
      icon: Package,
      title: 'Automated dispatch and replacement task creation',
      description:
        'When troubleshooting cannot resolve the issue, the AI automatically creates dispatch, repair, or replacement tasks in ITSM - routed to the correct fulfillment team with all diagnostic context.',
    },
    {
      icon: Database,
      title: 'Asset and warranty policy checks with smart escalation',
      description:
        'The AI validates warranty status and asset ownership in CMDB before recommending actions - ensuring correct entitlement and escalating complex cases with full diagnostic history.',
    },
  ];

  const capabilities = [
    'Integrate ITSM + CMDB/asset + endpoint tools + shipping/logistics + vendor/warranty',
    'Build troubleshooting flows: decision trees + KB grounding + scripts',
    'Configure policies: warranty checks, replacements, approvals',
    'Define handoff: escalation triggers and repair workflows',
    'Pilot top device issues → expand across fleet',
  ];

  const features = [
    {
      icon: ListChecks,
      title: 'Guided Troubleshooting Flows',
      desc: 'Walk users through structured decision-tree troubleshooting steps - grounded in the knowledge base and device-specific runbooks - to resolve issues before escalation.',
      gradient: 'from-indigo-500 to-violet-500',
    },
    {
      icon: Database,
      title: 'Asset & Warranty Lookup',
      desc: 'Query the CMDB to retrieve asset details, ownership, warranty status, and support entitlement in real time before recommending any repair or replacement path.',
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      icon: Package,
      title: 'Dispatch & Replacement Automation',
      desc: 'Create and route dispatch, repair, loaner, or replacement tasks automatically when troubleshooting is exhausted - with all diagnostic data pre-populated.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: Layers,
      title: 'CMDB Integration',
      desc: 'Keep asset records updated throughout the support lifecycle - logging diagnostic findings, repair actions, and replacement events directly in the CMDB.',
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      icon: Monitor,
      title: 'Endpoint Tool Integration',
      desc: 'Connect to endpoint management tools to retrieve device health data, run remote diagnostics, and push remediation scripts where policy permits.',
      gradient: 'from-rose-500 to-red-400',
    },
    {
      icon: ArrowRight,
      title: 'Escalation with Full Context',
      desc: 'Complex cases are escalated to L2 engineers with the complete diagnostic record - asset details, troubleshooting steps attempted, and the reason for escalation - in a single handoff.',
      gradient: 'from-orange-400 to-amber-400',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'User reports a device issue',
      desc: 'The employee contacts IT support via voice, chat, or the ITSM portal and describes the hardware issue - device type, symptoms, and business impact.',
    },
    {
      step: '02',
      title: 'AI collects asset tag, symptoms, and urgency',
      desc: 'The agent prompts for the asset tag or device serial number, gathers symptom details, and assesses urgency to prioritize the case correctly.',
    },
    {
      step: '03',
      title: 'Runs guided troubleshooting steps',
      desc: 'The agent walks the user through decision-tree troubleshooting - checking power, connectivity, driver state, and device-specific diagnostics based on KB runbooks.',
    },
    {
      step: '04',
      title: 'If unresolved, triggers dispatch, repair, or replacement workflow',
      desc: 'When troubleshooting is exhausted, the AI validates warranty status in CMDB and automatically creates the appropriate workflow - dispatch, repair center referral, loaner, or replacement.',
    },
    {
      step: '05',
      title: 'Updates ITSM and CMDB, notifies user',
      desc: 'The ITSM ticket is updated with full diagnostic history and action taken. The CMDB asset record is updated. The user receives a notification with next steps and expected timeline.',
    },
    {
      step: '06',
      title: 'Escalates complex cases with full context',
      desc: 'Cases requiring specialist judgment are escalated to L2 with the complete diagnostic record pre-populated - asset details, steps attempted, CMDB data, and warranty status.',
    },
  ];

  const stats = [
    { value: '~40%', label: 'L1 workload reduction' },
    { value: 'Faster', label: 'MTTR with structured triage' },
    { value: '<$5', label: 'Cost for routine cases (vs $25–$40)' },
    { value: '24/7', label: 'Troubleshooting coverage' },
  ];

  const faqs = [
    {
      q: 'Can it support different device vendors?',
      a: 'Yes. Troubleshooting flows are configurable per device vendor and model. Runbooks can be maintained for Dell, HP, Lenovo, Apple, and other vendors - with device-specific diagnostic steps and known-issue resolutions grounded in the knowledge base.',
    },
    {
      q: 'Can it trigger loaners or replacement devices?',
      a: 'Yes. When troubleshooting cannot resolve the issue, the agent checks CMDB for available loaners or initiates a replacement request - based on configured warranty and replacement policies. The task is created in ITSM and routed to the appropriate fulfillment team.',
    },
    {
      q: 'How does it integrate with warranty systems?',
      a: 'Digian.ai integrates with CMDB to retrieve warranty expiry and vendor support entitlement. For out-of-warranty devices, it can check vendor support portals (e.g., Dell ProSupport, HP Care Pack) via configured API integrations or knowledge base lookups to determine available options.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="useCaseHardwareSupport" />
      <JsonLd schema={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Home","item":"https://digian.ai"},{"@type":"ListItem","position":2,"name":"Use Cases","item":"https://digian.ai/use-cases"},{"@type":"ListItem","position":3,"name":"Hardware Support","item":"https://digian.ai/use-cases/hardware-support"}]}} />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm font-medium tracking-wide text-indigo-600 uppercase">IT Helpdesk / Hardware Troubleshooting &amp; Dispatch</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900">
              AI Hardware Support - Troubleshoot, Diagnose, Dispatch, Replace
            </h1>
            <p className="text-lg text-slate-600">
              AI provides guided troubleshooting, captures diagnostics, and orchestrates dispatch, replacement, and loaner workflows via ITSM, CMDB, endpoint tools, and logistics systems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo"><Button size="lg">Request a Demo</Button></Link>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              {['Structured triage + KB grounding', 'CMDB + warranty lookup', 'Automated dispatch workflows'].map(t => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />{t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Monitor className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-indigo-600 uppercase">Hardware Support Automation</p>
                <p className="text-sm text-slate-600">Triage · CMDB lookup · Dispatch · Escalation</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-slate-700">
              {capabilities.map((item, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <Monitor className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-indigo-600 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <p className="text-3xl font-extrabold text-white">{s.value}</p>
                <p className="text-indigo-200 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Why IT teams automate hardware support</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={b.title} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Capabilities</p>
            <h2 className="text-3xl font-bold text-slate-900">Everything needed to automate hardware support</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-4`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Process</p>
            <h2 className="text-3xl font-bold text-slate-900">How AI hardware support works at runtime</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.step} {...fadeUp} transition={{ delay: i * 0.12 }}
                className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="flex items-center gap-4 mb-5">
                  <span className="text-5xl font-black text-indigo-100">{s.step}</span>
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Monitor className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">From device issue to resolution - faster, with less L1 effort</h2>
            <p className="text-xl text-indigo-100">
              Guided triage. Automated dispatch. CMDB-verified actions. 24/7 hardware support - at scale.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo"><Button size="lg" variant="secondary">Request Demo</Button></Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Talk to Our Team <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="text-indigo-200 text-sm">CMDB + warranty · Guided troubleshooting · Automated dispatch</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HardwareSupportPage;
